import { useEffect, useRef, useState, type MutableRefObject, type PointerEvent } from 'react';
import { ArrowLeft, ArrowRight, ArrowUp } from 'lucide-react';
import type { InputState } from '../../shared/types';

type Action = 'left' | 'right' | 'jump';

export function TouchControls({ input, disabled, force }: {
  input: MutableRefObject<InputState>;
  disabled: boolean;
  force: boolean;
}) {
  const pointers = useRef(new Map<number, Action>());
  const keys = useRef(new Set<Action>());
  const [pressed, setPressed] = useState<Record<Action, boolean>>({ left: false, right: false, jump: false });

  function update(action: Action) {
    const held = [...pointers.current.values()].includes(action) || keys.current.has(action);
    input.current[action] = held;
    setPressed((previous) => ({ ...previous, [action]: held }));
  }

  function release(event: PointerEvent<HTMLButtonElement>) {
    const action = pointers.current.get(event.pointerId);
    if (!action) return;
    pointers.current.delete(event.pointerId);
    update(action);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  useEffect(() => {
    function releaseAll() {
      for (const action of new Set([...pointers.current.values(), ...keys.current])) input.current[action] = false;
      pointers.current.clear();
      keys.current.clear();
      setPressed({ left: false, right: false, jump: false });
    }
    const onVisibility = () => { if (document.hidden) releaseAll(); };
    if (disabled) releaseAll();
    window.addEventListener('blur', releaseAll);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      releaseAll();
      window.removeEventListener('blur', releaseAll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [disabled, input]);

  function control(action: Action, label: string) {
    return (
      <button type="button" className={`touch-button touch-${action}${pressed[action] ? ' is-pressed' : ''}`} disabled={disabled} aria-label={label} aria-pressed={pressed[action]}
        onPointerDown={(event) => {
          if (disabled || event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          pointers.current.set(event.pointerId, action);
          update(action);
        }}
        onPointerUp={release} onPointerCancel={release} onLostPointerCapture={release}
        onContextMenu={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if ((event.key === ' ' || event.key === 'Enter') && !disabled) {
            event.preventDefault();
            event.stopPropagation();
            keys.current.add(action);
            update(action);
          }
        }}
        onKeyUp={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            keys.current.delete(action);
            update(action);
          }
        }}
        onBlur={() => { keys.current.delete(action); update(action); }}>
        {action === 'left' ? <ArrowLeft aria-hidden="true" /> : action === 'right' ? <ArrowRight aria-hidden="true" /> : <><ArrowUp aria-hidden="true" /><span>Jump</span></>}
      </button>
    );
  }

  return (
    <div className="touch-controls" data-force={force} role="group" aria-label="On-screen movement controls">
      <div className="direction-buttons">{control('left', 'Move left')}{control('right', 'Move right')}</div>
      <span className="touch-center">Small hops.<br />Big teamwork.</span>
      <div className="jump-button-group">{control('jump', 'Jump')}</div>
    </div>
  );
}
